import React, { useEffect } from 'react'
import { FieldErrors, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import ControlledAutocompleteFacility from 'transfix-form/components/ControlledAutocompleteFacility'
import ControlledDateAndTime from 'transfix-form/components/ControlledDateAndTime'
import ControlledRadioGroupInput from 'transfix-form/components/ControlledRadioGroupInput'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { IAutocompleteFacilityProps } from 'transfix-ui/components/AutocompleteFacility'
import { IShipperFacilityResultFragment } from 'transfix-ui/components/AutocompleteFacility/graphql/ShipperFacilityResultFragment'
import { TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentStates, ITrailerStatesEnum } from 'types/graphqlTypes'
import { ICarrierResultFragment } from 'apps/marketplace/components/CarrierAutosuggest/graphql/CarrierResultFragment'
import ControlledCarrierAutosuggest from 'apps/marketplace/components/ControlledCarrierAutosuggest'
import { titleCase } from 'utils/titleCase'
import { IShipmentProgressStopFragment } from '../../../graphql/ShipmentProgressStopFragment'
import { TAddTrailerSchema } from './AddTrailerSchema'
import ControlledShipmentAutosuggest from './ControlledShipmentAutosuggest/ControlledShipmentAutosuggest'
import { IShipmentResultFragment } from './ShipmentAutosuggest/graphql/ShipmentResultFragment'
import { useEditTrailerInfoQuery } from './graphql/EditTrailerInfo'
import { useAddTrailerFormContext } from './useAddTrailerForm'

export interface IStatusOption {
  label: string
  id: string
}

export const trailerStatusToStatusOption = (status: ITrailerStatesEnum) => {
  return { label: titleCase(status.split('_').join(' ')), id: status }
}

const ALL_STATUS_OPTIONS: IStatusOption[] = Object.values(ITrailerStatesEnum).map(status =>
  trailerStatusToStatusOption(status)
)

const useTrailerFormStyles = makeStyles({
  statusLabel: {
    margin: 0,
  },
  error: {
    color: brandColors.error,
  },
})

const getErrorMessage = <T extends FieldErrors<TAddTrailerSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface IAddTrailerFormProps {
  stop?: IShipmentProgressStopFragment
  carrierApplication?: ICarrierResultFragment
  shipment?: IShipmentResultFragment
  shipperFacility?: IShipperFacilityResultFragment
  shipperApplicationId?: string
  mutationErrors: string | null
  showEmptyAtDelivery?: boolean
  shipmentDisabled?: boolean
  displayStatusOptionsGenerator?: (statuses: IStatusOption[]) => IStatusOption[]
}

export const AddTrailerForm = ({
  carrierApplication: carrierApplicationProp,
  displayStatusOptionsGenerator,
  mutationErrors,
  shipment: shipmentProp,
  shipmentDisabled = false,
  shipperApplicationId,
  shipperFacility: shipperFacilityProp,
  showEmptyAtDelivery = false,
  stop,
}: IAddTrailerFormProps) => {
  const { control, errors, setValue, watch } = useAddTrailerFormContext()

  const status = useWatch<ITrailerStatesEnum | null>({
    control,
    name: 'status',
  })

  const { shipment: shipmentId } = watch(['shipment'])
  const { data } = useEditTrailerInfoQuery({
    variables: {
      shipmentId,
    },
  })

  const { shipment: selectedShipment } = data || {}
  const { carrierApplication: selectedCarrier } = selectedShipment || {}

  const { dropTrailer, shipment } = stop || {}
  const { stops = [], carrierApplication } = shipment || {}
  const isLastStop = stop?.id === stops[stops.length - 1]?.id
  const { arrivedAt, mergedFacility } = stop || {}
  const { facility, shipperFacility: mergedShipperFacility } = mergedFacility || {}
  const shipperFacility: IAutocompleteFacilityProps['shipperFacility'] =
    mergedShipperFacility && facility
      ? {
          ...mergedShipperFacility,
          facility,
        }
      : undefined

  const defaultDisplayedStatusOptions =
    (dropTrailer && isLastStop && !!arrivedAt) || showEmptyAtDelivery
      ? ALL_STATUS_OPTIONS
      : ALL_STATUS_OPTIONS.filter(opt => opt.id !== ITrailerStatesEnum.empty_at_delivery)
  const displayedStatusOptions = displayStatusOptionsGenerator
    ? displayStatusOptionsGenerator(ALL_STATUS_OPTIONS)
    : defaultDisplayedStatusOptions

  const classes = useTrailerFormStyles()

  /**
   * When the user selects a shipment this is going to auto setup the carrier for its shipment
   */
  useEffect(() => {
    if (selectedCarrier) {
      setValue('carrier', selectedCarrier.id)
    } else {
      setValue('carrier', carrierApplicationProp?.id || carrierApplication?.id || '')
    }
  }, [setValue, selectedCarrier, carrierApplicationProp, carrierApplication])

  const hasCarrier = Boolean(selectedCarrier || carrierApplicationProp || carrierApplication)

  return (
    <form data-test='AddTrailerForm'>
      <Box my={1}>
        <ControlledCarrierAutosuggest
          name='carrier'
          control={control}
          carrier={selectedCarrier || carrierApplicationProp || carrierApplication}
          AutocompleteProps={{
            label: 'Carrier',
            disabled: Boolean(shipmentId) && hasCarrier,
            inputValidationMessage: getErrorMessage(errors, 'carrier'),
            inputError: !!getErrorMessage(errors, 'carrier'),
          }}
        />
      </Box>
      <Box my={1}>
        <ControlledTextField
          name='trailerNumber'
          control={control}
          TextInputProps={{
            label: 'Trailer #',
            validationMessage: getErrorMessage(errors, 'trailerNumber'),
            error: !!getErrorMessage(errors, 'trailerNumber'),
            placeholder: 'add new',
            dataTest: 'input-trailerNumber',
          }}
        />
      </Box>
      <Box my={1}>
        <label className={`label ${classes.statusLabel} required`} htmlFor='status'>
          Trailer Status
        </label>
        <ControlledRadioGroupInput
          name='status'
          control={control}
          options={displayedStatusOptions}
          RadioProps={{
            validationMessage: getErrorMessage(errors, 'status'),
          }}
        />
      </Box>
      {(status === ITrailerStatesEnum.assigned_to_shipment ||
        status === ITrailerStatesEnum.empty_at_delivery) && (
        <Box my={1}>
          <ControlledShipmentAutosuggest
            name='shipment'
            control={control}
            shipment={shipmentProp || shipment}
            filters={{
              state: [
                IShipmentStates.covered,
                IShipmentStates.tendered,
                IShipmentStates.offered,
                IShipmentStates.on_hold,
                IShipmentStates.assigned,
                IShipmentStates.dispatched,
                IShipmentStates.arrived_for_pickup,
                IShipmentStates.in_transit,
                IShipmentStates.arrived_for_delivery,
              ],
            }}
            AutocompleteProps={{
              label: 'Shipment',
              disabled: shipmentDisabled || Boolean(stop),
              inputValidationMessage: getErrorMessage(errors, 'shipment'),
              inputError: !!getErrorMessage(errors, 'shipment'),
            }}
          />
        </Box>
      )}
      {(status === ITrailerStatesEnum.assigned_to_facility ||
        status === ITrailerStatesEnum.empty_at_delivery) && (
        <Box my={1}>
          <ControlledAutocompleteFacility
            name='facility'
            control={control}
            shipperApplicationId={shipperApplicationId || ''}
            transform={({ id }) => id}
            shipperFacility={shipperFacilityProp || shipperFacility}
            AutocompleteFacilityProps={{
              label: 'Facility',
              disabled: Boolean(stop),
              inputValidationMessage: getErrorMessage(errors, 'facility'),
              inputError: !!getErrorMessage(errors, 'facility'),
              dataTest: 'input-facility',
            }}
          />
        </Box>
      )}
      {status === ITrailerStatesEnum.assigned_to_facility && (
        <Box my={1}>
          <ControlledDateAndTime
            name='arrivedAt'
            control={control}
            defaultValue={{ date: null }}
            DatePickerProps={{
              label: 'Arrived At',
              validationMessage:
                get(errors, 'arrivedAt.date.message') || get(errors, 'arrivedAt.message'),
              error: Boolean(
                get(errors, 'arrivedAt.date.message') || get(errors, 'arrivedAt.message')
              ),
              dataTest: 'input-arrivedAt',
              required: false,
              placeholder: 'mm/dd/yyyy',
              fullWidth: true,
            }}
            TimeInputProps={{
              label: 'Time',
              minuteIncrement: 15,
              timeFormat: TIME_INPUT_FORMAT.MILITARY,
              placeholder: '--:--',
              validationMessage:
                get(errors, 'arrivedAt.time.message') || get(errors, 'arrivedAt.message'),
              invalid: Boolean(
                get(errors, 'arrivedAt.time.message') || get(errors, 'arrivedAt.message')
              ),
              required: false,
            }}
          />
        </Box>
      )}
      {status === ITrailerStatesEnum.empty_at_delivery && (
        <Box my={1}>
          <ControlledDateAndTime
            name='emptiedAt'
            control={control}
            defaultValue={{ date: null }}
            DatePickerProps={{
              label: 'Emptied At',
              validationMessage:
                get(errors, 'emptiedAt.date.message') || get(errors, 'emptiedAt.message'),
              error: Boolean(
                get(errors, 'emptiedAt.date.message') || get(errors, 'emptiedAt.message')
              ),
              dataTest: 'input-emptiedAt',
              required: true,
              placeholder: 'mm/dd/yyyy',
              fullWidth: true,
            }}
            TimeInputProps={{
              label: 'Time',
              minuteIncrement: 15,
              timeFormat: TIME_INPUT_FORMAT.MILITARY,
              placeholder: '--:--',
              validationMessage:
                get(errors, 'emptiedAt.time.message') || get(errors, 'emptiedAt.message'),
              invalid: Boolean(
                get(errors, 'emptiedAt.time.message') || get(errors, 'emptiedAt.message')
              ),
              required: true,
            }}
          />
        </Box>
      )}
      <Box my={1}>
        <ControlledTextField
          name='trailerNotes'
          control={control}
          TextInputProps={{
            label: 'Trailer Notes',
            validationMessage: getErrorMessage(errors, 'trailerNotes'),
            error: !!getErrorMessage(errors, 'trailerNotes'),
            placeholder: 'location, notes, etc.',
            dataTest: 'input-trailerNotes',
          }}
        />
      </Box>
      {mutationErrors && (
        <Box my={1}>
          <Typography className={classes.error}>{mutationErrors}</Typography>
        </Box>
      )}
    </form>
  )
}
